/**
 * Canonical URLs for the public marketing site.
 *
 * The same Cloudflare Pages project serves waypoint-intel.com,
 * www.waypoint-intel.com and app.waypoint-intel.com, so every marketing page is
 * reachable on three hosts. Without a canonical, Search Console sees the copies
 * as duplicates and may index the app host instead of the marketing one.
 *
 * WHY AT RUNTIME, NOT IN index.html: this is a single-page app, so every route
 * is served the same index.html. A static <link rel="canonical"> there would
 * point every page at the homepage, which tells Google the whole site is one
 * page -- worse than having none. The tag is set per route from
 * MarketingLayout, and removed when the visitor leaves the marketing site so
 * sign-in and dashboard pages never inherit one. Google renders JavaScript
 * before indexing and honours a canonical added this way.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** The host Search Console should index. Matches public/sitemap.xml. */
export const CANONICAL_ORIGIN = 'https://waypoint-intel.com';

/**
 * Absolute canonical URL for a path: canonical origin, no query string or
 * fragment (UTM tags and anchors are the same page), no trailing slash except
 * on the root.
 */
export function canonicalUrl(pathname: string): string {
  let path = pathname.split(/[?#]/)[0] ?? '';
  if (!path.startsWith('/')) path = '/' + path;
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return CANONICAL_ORIGIN + (path || '/');
}

/** Keep <link rel="canonical"> and og:url in step with the current route. */
export function useCanonical(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const href = canonicalUrl(pathname);

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const created = !link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = href;

    // og:url is what link previews and social crawlers treat as the page's
    // address; index.html ships it pointing at the homepage for every route.
    const og = document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const previousOg = og?.content;
    if (og) og.content = href;

    return () => {
      if (created) link?.remove();
      if (og && previousOg !== undefined) og.content = previousOg;
    };
  }, [pathname]);
}
